import { useEffect, type FC, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Space } from '@g2u/ui-kit';
import { Form } from 'antd';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getCancelationReasons, putJobCancel } from 'queries';

import { Content, SendNotification } from 'components';
import { Condition, type JsonDataItem } from 'types';
import { RootStore } from 'store';
import { Toast, parseErrors, validateFormSilently } from 'helpers';
import { SelectCancelReason } from './components';
import styles from './cancel.module.scss';

enum CancelationStep {
  SelectReason,
  SendNotification,
}

export const JobCancelPage: FC = observer(() => {
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [selectedReason, setSelectedReason] = useState<number>();
  const [providedDescription, setProvidedDescription] = useState<string>();
  const [step, setStep] = useState<CancelationStep>(CancelationStep.SelectReason);
  const { setBreadcrumbs } = RootStore;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [cancelationForm] = Form.useForm();
  const reasonSelection = step === CancelationStep.SelectReason;
  const sendNotification = step === CancelationStep.SendNotification;
  Form.useWatch([], { form: cancelationForm });
  const returnTo = location.state?.returnTo || '/job/search';
  const [redirectLink] = useState<string>(returnTo);

  const { data: cancelationReasons, isInitialLoading: isReasonsLoading } = useQuery(
    ['get-cancelation-reasons'],
    getCancelationReasons,
    {
      onSuccess: () => {
        setStep(CancelationStep.SelectReason);
      },
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    },
  );

  const { mutate: cancelJob, isLoading: cancellingJob } = useMutation(putJobCancel, {
    onSuccess: () => {
      cancelationForm.resetFields();
      navigate(redirectLink);
      Toast.show({
        description: (
          <>
            Job cancelled!
            <br />
            Notifications have been sent.
          </>
        ),
        type: Toast.Type.Success,
      });
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const handleFormFinish = (data: any) => {
    cancelJob({
      notifications: data.notifications,
      jobId: id,
      reasonId: selectedReason,
      charges: location.state.charges || Condition.Yes,
      description: selectedReason === 6 ? providedDescription : undefined,
    });
  };

  const handleFormChange = (data: JsonDataItem) => {
    // manually save data from the unmounted fields
    data.reason && setSelectedReason(data.reason);
    data.description && setProvidedDescription(data.description);
    if (data.reason === 6) {
      setIsFormValid(!!cancelationForm.getFieldValue('description'));
    } else {
      void validateFormSilently(cancelationForm, setIsFormValid);
    }
  };

  useEffect(() => {
    setBreadcrumbs([
      {
        url: '/job/search',
        title: 'Find a job',
      },
      {
        title: 'Cancel',
      },
    ]);

    return () => {
      setBreadcrumbs(null);
    };
  }, []);

  return (
    <Space.$ fullWidth direction={Space.Direction.Vertical}>
      <title>Cancel job</title>
      <Content dataTestId="job-cancelation-page" className={styles.cancel}>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
          <Form
            preserve={true}
            form={cancelationForm}
            layout="horizontal"
            onFinish={handleFormFinish}
            onValuesChange={handleFormChange}
            colon={false}
          >
            {reasonSelection && (
              <SelectCancelReason
                reasons={cancelationReasons}
                isReasonsLoading={isReasonsLoading}
                form={cancelationForm}
              />
            )}
            {sendNotification && <SendNotification isShort={true} />}
          </Form>

          <Space.$ justify={Space.Justify.End} size={Space.Size.Middle}>
            <Button.$
              dataTestId="cancel-btn"
              onClick={() => {
                reasonSelection && navigate('/job/search');
                sendNotification && setStep(CancelationStep.SelectReason);
              }}
            >
              {reasonSelection && 'Cancel'}
              {sendNotification && <>&lt; Back</>}
            </Button.$>
            <Button.$
              type={Button.Type.Primary}
              dataTestId="cancel-job-btn"
              onClick={() => {
                reasonSelection && setStep(CancelationStep.SendNotification);
                sendNotification && cancelationForm.submit();
              }}
              disabled={!isFormValid}
              loading={cancellingJob}
            >
              {reasonSelection && <>Confirm &gt;</>}
              {sendNotification && <>Send Notifications</>}
            </Button.$>
          </Space.$>
        </Space.$>
      </Content>
    </Space.$>
  );
});
